import type { LimitsView } from "../api/types.js";
import { dateTime, formatMoney } from "../lib/format.js";

/**
 * DCTA-154 — the Limits page, from `GET /v1/limits` alone, in UX spec §F.2 row 21's words (21u–21w, DCTA-155; F26).
 * Backend DCTA-153 ruling 3: a payment's cap refusal names no figure and points here instead, so the customer reads
 * their limits on the bank's own page — never in the chat, never read aloud.
 *
 * Read only. Every figure is the bank's decimal string, grouped and labelled by `formatMoney` — nothing is added,
 * subtracted or worked out, so there is no "left today". A figure the read does not carry has no row (never a zero,
 * never a default), and a help line goes with the row it explains. The new-payee wait's hours come with B35
 * (`limits.new_payee_wait_hours`): until the read carries them there is no wait row — no number is invented (12g). DCTA-198
 * (ask 154-yy): the read's `currency` labels every figure; an older bank's read without it is in Singapore dollars.
 */
export type ValuePart = string | { readonly money: string };

export interface LimitRow {
  readonly key: "payment" | "day" | "daily" | "sent" | "first" | "wait" | "timer";
  readonly label: string;
  /** Read after the label: words, and the bank's figure in the money face. */
  readonly value: readonly ValuePart[];
}

export interface LimitSection {
  readonly title: string;
  readonly rows: readonly LimitRow[];
  /** The line under the list, when the row it explains is there. */
  readonly help?: string;
}

export interface LimitsModel {
  readonly sections: readonly LimitSection[];
  /** 21w: what can be done, and who does it. */
  readonly last: string;
}

/** An older bank's read carries no `currency` (before DCTA-191): its money was always Singapore dollars. */
const OLDER_BANK_CURRENCY = "SGD";

/** A whole, positive number of hours (`"12"` or `12`, as `policy.cooling_hours` is signed), or none. */
function wholeHours(v: string | number | undefined): string | undefined {
  const s = typeof v === "number" ? String(v) : v;
  return s !== undefined && /^[1-9]\d*$/.test(s) ? s : undefined;
}

/**
 * 21u's help line. DCTA-162 F28 (DCTA-155 round 2): with the one-payment threshold in the read, the line names it, so the
 * card's "A payment this size" (21z) points at a figure the customer can find here.
 */
function tokenHelp(threshold: string | undefined, currency: string): string {
  return threshold === undefined
    ? "Above your daily transfer limit, you also confirm with your security token."
    : `Above your daily transfer limit, or above ${formatMoney(threshold, currency)} in one payment, you also confirm with your security token.`;
}

export function limitsModel(read: LimitsView, nowMs: number): LimitsModel {
  const l = read.limits;
  const currency = read.currency ?? OLDER_BANK_CURRENCY;
  const money = (v: string): ValuePart => ({ money: formatMoney(v, currency) });
  const out: LimitRow[] = [];
  if (l.per_transaction_max !== undefined) out.push({ key: "payment", label: "In one payment", value: ["Up to ", money(l.per_transaction_max)] });
  if (l.daily_third_party_ceiling !== undefined) out.push({ key: "day", label: "In a day", value: ["Up to ", money(l.daily_third_party_ceiling)] });
  if (l.daily_third_party_allowance !== undefined) out.push({ key: "daily", label: "Your daily transfer limit", value: [money(l.daily_third_party_allowance)] });
  const sent = read.counters.daily_third_party_used;
  if (sent !== undefined) out.push({ key: "sent", label: "Sent today", value: [money(sent)] });

  const payees: LimitRow[] = [];
  if (l.new_payee_first_payment_max !== undefined) payees.push({ key: "first", label: "To someone you haven't paid before", value: ["Up to ", money(l.new_payee_first_payment_max)] });
  // 21v / B35: the wait's hours as the bank sends them — a whole number, or no row (never a constant, 12g).
  const hours = wholeHours(l.new_payee_wait_hours);
  if (hours !== undefined) payees.push({ key: "wait", label: "Wait after you add a payee", value: [`${hours} hours`] });
  // One row per payee the bank says is still waiting (its clock), named as the bank names them (the registered name).
  // F26: §E.6's words as 17 Payees prints them — "can go out", never a payment that is scheduled.
  for (const t of read.cooling_timers ?? []) {
    if (Date.parse(t.until) > nowMs) payees.push({ key: "timer", label: t.subject, value: [`Payments can go out from ${dateTime(t.until)}`] });
  }

  return {
    sections: [
      {
        title: "How much can go out",
        rows: out,
        ...(l.daily_third_party_allowance === undefined ? {} : { help: tokenHelp(l.step_up_threshold, currency) }),
      },
      {
        title: "New payees",
        rows: payees,
        ...(l.new_payee_first_payment_max === undefined ? {} : { help: "Money already waiting to go to someone you haven't paid before counts toward their limit." }),
      },
    ],
    last: "To lower your daily transfer limit, ask the assistant. To raise a limit, talk to us.",
  };
}
